import * as React from 'react';
import {
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTranslation } from 'react-i18next';

/**
 * DataTable - Composant générique de table de données avec tri, filtrage et pagination
 *
 * @template TData - Type des données
 * @template TValue - Type des valeurs des colonnes
 *
 * @param {Object} props
 * @param {import('@tanstack/react-table').ColumnDef<TData, TValue>[]} props.columns - Définitions des colonnes
 * @param {TData[]} props.data - Données à afficher
 * @param {function(TData): void} [props.onRowClick] - Callback lors du clic sur une ligne
 * @param {string} [props.emptyMessage] - Message à afficher quand il n'y a pas de données
 * @param {React.ReactNode} [props.emptyIcon] - Icône à afficher quand il n'y a pas de données
 * @param {boolean} [props.showPagination=true] - Afficher la pagination
 * @param {number} [props.pageSize=10] - Nombre d'éléments par page par défaut
 * @param {string} [props.className] - Classes CSS additionnelles
 *
 * Server-side pagination props:
 * @param {boolean} [props.manualPagination=false] - Activer la pagination côté serveur
 * @param {number} [props.pageCount] - Nombre total de pages (requis si manualPagination=true)
 * @param {number} [props.currentPage=1] - Page courante (1-indexed, pour manualPagination)
 * @param {function(number): void} [props.onPageChange] - Callback lors du changement de page (reçoit le numéro de page 1-indexed)
 * @param {function(number): void} [props.onPageSizeChange] - Callback lors du changement de taille de page
 * @param {number} [props.totalCount] - Nombre total d'éléments (pour affichage, optionnel)
 * @param {boolean} [props.isLoading=false] - Afficher un indicateur de chargement
 */
export function DataTable({
    columns,
    data,
    onRowClick,
    emptyMessage = 'Aucun résultat.',
    emptyIcon,
    showPagination = true,
    pageSize = 10,
    className = '',
    hideHeader = false,
    // Server-side pagination props
    manualPagination = false,
    pageCount,
    currentPage = 1,
    onPageChange,
    onPageSizeChange,
    totalCount,
    isLoading = false,
    // Server-side sorting props
    manualSorting = false,
    sorting: externalSorting,
    onSortingChange: externalOnSortingChange,
}) {
    const [internalSorting, setInternalSorting] = React.useState([]);
    const sorting = manualSorting && externalSorting !== undefined ? externalSorting : internalSorting;
    const handleSortingChange = (updater) => {
        const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
        if (manualSorting && externalOnSortingChange) {
            externalOnSortingChange(newSorting);
        } else {
            setInternalSorting(newSorting);
        }
    };
    const [columnFilters, setColumnFilters] = React.useState([]);
    const [columnVisibility, setColumnVisibility] = React.useState({});
    const [pagination, setPagination] = React.useState({
        pageIndex: currentPage - 1,
        pageSize,
    });

    // Sync pagination state with currentPage prop (for server-side pagination)
    React.useEffect(() => {
        if (manualPagination && currentPage !== pagination.pageIndex + 1) {
            setPagination((prev) => ({ ...prev, pageIndex: currentPage - 1 }));
        }
    }, [currentPage, manualPagination, pagination.pageIndex]);

    const table = useReactTable({
        data,
        columns,
        onSortingChange: handleSortingChange,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        ...(manualPagination ? {} : { getPaginationRowModel: getPaginationRowModel() }),
        ...(manualSorting ? { manualSorting: true } : { getSortedRowModel: getSortedRowModel() }),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        ...(manualPagination
            ? {
                  manualPagination: true,
                  pageCount: pageCount ?? -1,
                  onPaginationChange: (updater) => {
                      const newPagination = typeof updater === 'function' ? updater(pagination) : updater;
                      setPagination(newPagination);
                      if (newPagination.pageIndex !== pagination.pageIndex) {
                          onPageChange?.(newPagination.pageIndex + 1);
                      }
                      if (newPagination.pageSize !== pagination.pageSize) {
                          onPageSizeChange?.(newPagination.pageSize);
                      }
                  },
              }
            : {}),
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            ...(manualPagination ? { pagination } : {}),
        },
        initialState: {
            pagination: {
                pageSize,
                pageIndex: currentPage - 1,
            },
        },
    });

    return (
        <>
            <Table className={className}>
                {!hideHeader && (
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    const hideOnMobile = header.column.columnDef.meta?.hideOnMobile;
                                    const showOnMobileOnly = header.column.columnDef.meta?.showOnMobileOnly;
                                    const cellClassName = header.column.columnDef.meta?.className ?? '';
                                    const responsiveClass = hideOnMobile
                                        ? 'hidden md:table-cell'
                                        : showOnMobileOnly
                                          ? 'md:hidden'
                                          : '';
                                    return (
                                        <TableHead
                                            key={header.id}
                                            className={[responsiveClass, cellClassName].filter(Boolean).join(' ')}
                                        >
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(header.column.columnDef.header, header.getContext())}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                )}
                <TableBody>
                    {table.getRowModel().rows?.length ? (
                        table.getRowModel().rows.map((row) => (
                            <TableRow
                                key={row.id}
                                data-state={row.getIsSelected() && 'selected'}
                                className={onRowClick ? 'cursor-pointer hover:bg-surface-hover' : ''}
                                onClick={() => onRowClick?.(row.original)}
                            >
                                {row.getVisibleCells().map((cell) => {
                                    const hideOnMobile = cell.column.columnDef.meta?.hideOnMobile;
                                    const showOnMobileOnly = cell.column.columnDef.meta?.showOnMobileOnly;
                                    const cellClassName = cell.column.columnDef.meta?.className ?? '';
                                    const responsiveClass = hideOnMobile
                                        ? 'hidden md:table-cell'
                                        : showOnMobileOnly
                                          ? 'md:hidden'
                                          : '';
                                    return (
                                        <TableCell
                                            key={cell.id}
                                            className={[responsiveClass, cellClassName].filter(Boolean).join(' ')}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={columns.length} className="text-center">
                                {isLoading ? (
                                    <div className="flex items-center justify-center py-8">
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center">
                                        {emptyIcon && <div className="mb-4">{emptyIcon}</div>}
                                        <p className="text-muted-foreground">{emptyMessage}</p>
                                    </div>
                                )}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>

            {showPagination && data.length > 0 && (
                <DataTablePagination
                    table={table}
                    isLoading={isLoading}
                    totalCount={manualPagination ? totalCount : undefined}
                    manualPagination={manualPagination}
                />
            )}
        </>
    );
}

/**
 * Composant de pagination pour DataTable
 * @param {Object} props
 * @param {import('@tanstack/react-table').Table} props.table - Instance de la table
 * @param {boolean} [props.isLoading=false] - Afficher un indicateur de chargement
 * @param {number} [props.totalCount] - Nombre total d'éléments (pour pagination serveur)
 * @param {boolean} [props.manualPagination=false] - Mode pagination serveur
 */
function DataTablePagination({ table, isLoading = false, totalCount, manualPagination = false }) {
    const { t } = useTranslation();
    const pageInfo = isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
    ) : (
        <span className="text-sm whitespace-nowrap">
            {t('common:page')} {table.getState().pagination.pageIndex + 1}/{table.getPageCount()}
        </span>
    );

    const itemCount =
        manualPagination && totalCount !== undefined ? totalCount : table.getFilteredRowModel().rows.length;

    return (
        <div className="flex flex-col gap-4 px-2 py-4 md:flex-row md:items-center md:justify-between">
            <div className="text-sm text-muted-foreground text-center md:text-left">{itemCount} {t('common:elementS')}</div>
            <div className="flex flex-col items-center gap-4 md:flex-row md:gap-6 lg:gap-8">
                <div className="hidden md:flex items-center space-x-2">
                    <p className="text-sm">{t('common:lignesParPage')}</p>
                    <Select
                        value={`${table.getState().pagination.pageSize}`}
                        onValueChange={(value) => {
                            table.setPageSize(Number(value));
                        }}
                    >
                        <SelectTrigger className="h-8 w-[70px]">
                            <SelectValue placeholder={table.getState().pagination.pageSize} />
                        </SelectTrigger>
                        <SelectContent side="top">
                            {[10, 20, 30, 40, 50].map((pageSize) => (
                                <SelectItem key={pageSize} value={`${pageSize}`}>
                                    {pageSize}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="hidden md:flex items-center justify-center">{pageInfo}</div>
                <div className="flex items-center space-x-2">
                    <Button
                        variant="outline"
                        color="neutral"
                        size="auto"
                        className="hidden size-8 lg:flex"
                        onClick={() => table.setPageIndex(0)}
                        disabled={!table.getCanPreviousPage()}
                    >
                        <span className="sr-only">{t('common:premierePage')}</span>
                        <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        color="neutral"
                        size="auto"
                        className="size-8"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        <span className="sr-only">{t('common:pagePrecedente')}</span>
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="mobile-only md:hidden">{pageInfo}</span>
                    <Button
                        variant="outline"
                        color="neutral"
                        size="auto"
                        className="size-8"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        <span className="sr-only">{t('common:pageSuivante')}</span>
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        color="neutral"
                        size="auto"
                        className="hidden size-8 lg:flex"
                        onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                        disabled={!table.getCanNextPage()}
                    >
                        <span className="sr-only">{t('common:dernierePage')}</span>
                        <ChevronsRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
}

export { DataTablePagination };
